import { type FpPaymentInfo } from '@hooks-api/types/dataModel/fpOrder'
import { Box } from '@pets-ui/Box/Box'
import { Button } from '@pets-ui/Button/Button'
import { HStack } from '@pets-ui/HStack/HStack'
import { Check, Copy } from '@pets-ui/icons'
import { UnorderedList } from '@pets-ui/List/UnorderedList'
import { type SnackbarVariantsProps } from '@pets-ui/Snackbar/SnackbarVariants'
import { useSnackbar } from '@pets-ui/Snackbar/useSnackbar'
import { Text } from '@pets-ui/Text/Text'
import { colors } from '@pets-ui/tokens/colors.stylex'
import { VStack } from '@pets-ui/VStack/VStack'
import * as stylex from '@stylexjs/stylex'
import { copyText } from '@utils'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

const COPY_FEEDBACK_DURATION = 3000

interface AtmPaymentInfoProps {
  paymentInfo?: FpPaymentInfo | null
}

export function AtmPaymentInfo(props: AtmPaymentInfoProps) {
  const { paymentInfo } = props
  const { openSnackbar } = useSnackbar<SnackbarVariantsProps>()
  const [isCopied, setIsCopied] = useState(false)
  const { t } = useTranslation()
  const handleCopyText = async () => {
    await copyText(paymentInfo?.account || '')
    setIsCopied(true)
    openSnackbar({ severity: 'success', message: '已複製 ATM 繳費帳號', autoHideDuration: COPY_FEEDBACK_DURATION })
    setTimeout(() => setIsCopied(false), COPY_FEEDBACK_DURATION)
  }

  return (
    <Box borderColor={colors.grey300} borderWidth={1} borderStyle="solid" borderRadius={4}>
      <VStack gap={2} backgroundColor={colors.blueGrey50} borderTopLeftRadius={4} borderTopRightRadius={4} p={3}>
        <HStack justifyContent="space-between">
          <Text variant="body" color={colors.grey600}>{t('orderDetail.transferBank', { defaultValue: '轉帳銀行' })}</Text>
          <Text variant="body" color={colors.grey800} fontWeight="600">
            {paymentInfo?.bank_name && paymentInfo?.bank_code
              ? `${paymentInfo?.bank_name} (${paymentInfo?.bank_code})`
              : paymentInfo?.bank_name || paymentInfo?.bank_code || '-'}
          </Text>
        </HStack>
        <HStack justifyContent="space-between">
          <Text variant="body" color={colors.grey600}>{t('orderDetail.atmAccountNumber', { defaultValue: 'ATM 繳費帳號' })}</Text>
          <HStack gap={1} alignItems="center">
            <Text variant="body" color={colors.grey800} fontWeight="600">{paymentInfo?.account || '-'}</Text>
            <Button variant="text" onClick={handleCopyText}>
              {isCopied ? <Check color={colors.green600} fontSize={16} /> : <Copy color={colors.grey600} fontSize={16} />}
            </Button>
          </HStack>
        </HStack>
        <HStack justifyContent="space-between">
          <Text variant="body" color={colors.grey600}>{t('orderDetail.paymentDeadline', { defaultValue: '繳費截止日期' })}</Text>
          <Text variant="body" color={colors.red500} fontWeight="600">{paymentInfo?.expire_date || '-'}</Text>
        </HStack>
      </VStack>
      <VStack pl={3} pr={2} pt={2}>
        <UnorderedList
          color={colors.grey600}
          fontSize="12px"
          list={[
            t('orderDetail.accountNotExist', { defaultValue: '若顯示「帳號不存在」屬正常，核對帳號無誤即可付款' }),
            t('orderDetail.unsupportedPaymentMethods', { defaultValue: '不支援 ATM 櫃員機無卡存款及銀行臨櫃繳款 (含無摺存款)' }),
            t('orderDetail.interbankFeeNotice', { defaultValue: '若使用與上述銀行不同的銀行帳號或 ATM 轉帳，將收取跨行手續費 15 元' }),
          ]}
        />
      </VStack>
    </Box>
  )
}

const noticeStyles = stylex.create({
  li: {
    color: colors.grey600,
    fontSize: 8,
    listStylePosition: 'outside',
  },
  ul: {
    paddingLeft: 16,
  },
})

export function Notice() {
  const { t } = useTranslation()
  return (
    <VStack gap={2}>
      <Text variant="caption" color={colors.grey600}>{t('orderDetail.paymentNoteTitle', { defaultValue: '注意事項：' })}</Text>
      <ul {...stylex.props(noticeStyles.ul)}>
        <li {...stylex.props(noticeStyles.li)}>
          <Text variant="caption" color={colors.grey600}>
            {t('orderDetail.paymentNoteDetail', { defaultValue: '持以上銀行發卡之金融卡並選擇該銀行，當使用該銀行 ATM歸員機繳費即免跨行手續費；熱選擇其他銀行ATM 櫃員雞 .需支付跨行手續費 15 元。' })}
            <br />
            <br />
            {t('orderDetail.paymentNoteExample', { defaultValue: '例：使用台灣銀行金融卡時，選擇台灣銀行 ATM 轉帳享 0 元手續費；選擇非台灣銀行需繳 15 元手續費/筆' })}
          </Text>
        </li>
        <br />
        <li {...stylex.props(noticeStyles.li)}>
          <Text variant="caption" color={colors.grey600}>
            {t('orderDetail.paymentNoteLimit', { defaultValue: '此付款方式不支援 ATM 櫃員機無卡存款功能及銀行臨櫃繳款(含無摺存款)' })}
          </Text>
        </li>
      </ul>
    </VStack>
  )
}
